import {
  setupMultiApiClient,
  createApiConfig,
  createHeaderRule,
  createTokenStorage,
  type ApiError,
  type RefreshTokenResponse,
  ApiErrorType,
} from "@oneapp/data";
import { useAuthStore, type EnrolledProgram } from "../store/useAuthStore";

const apiEndpoints = {
  learnGateway: import.meta.env.VITE_LEARN_GATEWAY || "",
  comService: import.meta.env.VITE_COM_SERVICE || "",
  oms: import.meta.env.VITE_OMS || "",
  oneOms: import.meta.env.VITE_ONE_OMS || "",
  cms: import.meta.env.VITE_CMS || "",
  oneCms: import.meta.env.VITE_ONE_CMS || "",
  assetRendering: import.meta.env.VITE_ASSET_RENDERING || "",
  masterData: import.meta.env.VITE_MASTER_DATA || "",
  tenantManagement: import.meta.env.VITE_TENANT_MANAGEMENT || "",
  newQB: import.meta.env.VITE_NEWQB || "",
  assessment: import.meta.env.VITE_ASSESSMENTS || "",
  oneAsm: import.meta.env.VITE_ONE_ASM || "",
  doubts: import.meta.env.VITE_DOUBTS || "",
  doubtsQB: import.meta.env.VITE_DOUBTS_QB || "",
  /** AI service (meta/subjects, bookmarks, etc.) — must match Orval `clientName: "doubtsAi"`. */
  doubtsAI: import.meta.env.VITE_DOUBTS_AI || "",
  /** DC API (create doubt, OCR, generate solution) — `clientName: "doubtsDCAPI"`. */
  doubtsDCAPI:
    import.meta.env.VITE_DOUBTS_DCAPI || import.meta.env.VITE_DOUBTS || "",
  wizklub: import.meta.env.VITE_WIZKLUB || "",
  peekmind: import.meta.env.VITE_PEEKMIND || "",
  peekMindReport: import.meta.env.VITE_PEEKMIND_REPORT || "",
  peekmindOnResume: import.meta.env.VITE_PEEKMIND_ONRESUME || "",
  newPeekmindToken: import.meta.env.VITE_NEW_PEEKMIND_TOKEN || "",
  newPeekmindQuiz: import.meta.env.VITE_NEW_PEEKMIND_QUIZ || "",
  learn: import.meta.env.VITE_LEARN || "",
  liveclass: import.meta.env.VITE_LIVECLASS || "",
  liveclassSocket: import.meta.env.VITE_LIVECLASS_SOCKET || "",
  eos: import.meta.env.VITE_EOS || "",
  s3Static: import.meta.env.VITE_S3_STATIC || "",
  oneApp: import.meta.env.VITE_ONE_LEARN || "",
  aina: import.meta.env.VITE_AINA || "",
  uam: import.meta.env.VITE_UAM || "",
  myActivity: import.meta.env.VITE_MYACTIVITY || "",
  dataReport: import.meta.env.VITE_DATA_REPORT || "",
  search: import.meta.env.VITE_SEARCH || "",
  selflearn: import.meta.env.VITE_SL_BASE_URL || "",
  selflearnWebSocket: import.meta.env.VITE_SL_WEBSOCKET || "",
  playlistVideo: import.meta.env.VITE_LIVECLASS || "",
  playlistReport: import.meta.env.VITE_LIVECLASS || "",
  newasm: import.meta.env.VITE_NEWASM || "",
  liveclassapiv2: import.meta.env.VITE_LIVE_CLASS_API_V2 || "",
  lsqManagement: import.meta.env.VITE_LSQ_MANAGEMENT || "",
  lsqService: `${(
    import.meta.env.VITE_LSQ_SERVICE || "https://lsqservice.infinitylearn.com"
  ).replace(/\/+$/, "")}/api/v1`,
  testlms: import.meta.env.VITE_TESTLMS || "",
  assignmentReport: import.meta.env.VITE_ASSIGNMENT_REPORT || "",
  oneGenius: import.meta.env.VITE_ONE_GENIUS || "",
};

const isDev = import.meta.env.DEV;

const getTenant = (): string => {
  const tenantFromUrl = localStorage.getItem("tenantFromUrl");
  const selectedTab = localStorage.getItem("SelectedTab");

  if (
    tenantFromUrl === "iltsc" &&
    (selectedTab === "subscribedPackages" || selectedTab === "enrollnow")
  ) {
    return "ILTSC";
  }

  return "infinitylearn";
};

export const getUserDetails = () => {
  try {
    const userDetails = localStorage.getItem("userDetails");
    return userDetails ? JSON.parse(userDetails) : null;
  } catch {
    return null;
  }
};

const getDeviceId = (): string => {
  return localStorage.getItem("deviceIp") || "127.0.0.1";
};

export const getUserId = (): string | null => {
  try {
    const mobileAndName = localStorage.getItem("mobile_and_name");
    if (mobileAndName) {
      const parsed = JSON.parse(mobileAndName);
      return parsed.userId?.toString() || null;
    }
  } catch {
    // Ignore errors
  }
  return null;
};

/** Default-profile UAM for headers; must match `mapModuleDataToDoubtSession` body `userId`. */
export const userId = (): string | null => {
  try {
    const profiles =
      useAuthStore.getState()?.profilesData?.unified_account_details
        ?.profiles ?? [];
    const profile = profiles.find((p) => p.is_default_profile) ?? profiles[0];
    const id = profile?.uam_id;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
};

export const mobileNumber = (): string | null => {
  try {
    return (
      useAuthStore.getState()?.profilesData?.unified_account_details
        ?.user_account_details?.phone ?? null
    );
  } catch {
    return null;
  }
};

const getAccessToken = (): string | null => useAuthStore.getState().token;

const getBearerToken = (): string | null => {
  const token = getAccessToken();
  return token ? `Bearer ${token}` : null;
};

export const getCurrentProgram = (): EnrolledProgram | null => {
  try {
    return (
      useAuthStore
        .getState()
        .enrolledPrograms?.find((p) => p.isDefault === true) ?? null
    );
  } catch {
    return null;
  }
};

/** Body for `POST /api/v3/unified/refresh-profile-token` (ProfileRefreshTokenRequestV3). */
function buildRefreshProfileTokenV3Body(): {
  refresh_token: string;
  unified_account_id: string;
  uam_id: number;
} {
  const state = useAuthStore.getState();
  const refreshToken = state.refreshToken;
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const unifiedAccountId =
    state.profilesData?.unified_account_details?.unified_account_id ??
    state.accountsData?.unified_accounts?.[0]?.unified_account_id;

  const profiles = state.profilesData?.unified_account_details?.profiles ?? [];
  const profile = profiles.find((p) => p.is_default_profile) ?? profiles[0];
  const rawUamId = profile?.uam_id;

  if (!unifiedAccountId) {
    throw new Error("Missing unified account id for token refresh");
  }
  if (rawUamId == null) {
    throw new Error("Missing uam id for token refresh");
  }

  const uam_id = Number(rawUamId);
  if (!Number.isFinite(uam_id)) {
    throw new Error("Invalid uam id for token refresh");
  }

  return {
    refresh_token: refreshToken,
    unified_account_id: unifiedAccountId,
    uam_id,
  };
}

/** Default or first enrolled program (same logic as token refresh / program headers). */
export function getDefaultEnrolledProgram(): EnrolledProgram | null {
  const programs = useAuthStore.getState().enrolledPrograms ?? [];
  return programs.find((p) => p.isDefault === true) ?? programs[0] ?? null;
}

/**
 * Must match `tenantId` in doubt API JSON bodies (enrolled program), not profile UUID,
 * or doubtsai meta calls can 401.
 */
function getTenentIdForDoubtsApis(): string {
  try {
    const program = getDefaultEnrolledProgram();
    if (program?.tenant_id != null && String(program.tenant_id).trim() !== "") {
      return String(program.tenant_id);
    }
    const profiles =
      useAuthStore.getState().profilesData?.unified_account_details?.profiles ??
      [];
    const p = profiles.find((x) => x.is_default_profile) ?? profiles[0];
    return p?.tenant_id?.toString() ?? "";
  } catch {
    return "";
  }
}

export function initializeApi(): void {
  setupMultiApiClient({
    apiConfig: createApiConfig({
      defaultEndpoint: "learnGateway",
      endpoints: {
        learnGateway: {
          name: "learnGateway",
          baseURL: apiEndpoints.learnGateway,
          description: "Learn Gateway service",
        },
        comService: {
          name: "comService",
          baseURL: apiEndpoints.comService,
          description: "Communication service",
        },
        oms: {
          name: "oms",
          baseURL: apiEndpoints.oms,
          description: "OMS service",
        },
        oneOms: {
          name: "oneOms",
          baseURL: apiEndpoints.oneOms,
          description: "One OMS service",
        },
        cms: {
          name: "cms",
          baseURL: apiEndpoints.cms,
          description: "CMS service",
        },
        oneCms: {
          name: "oneCms",
          baseURL: apiEndpoints.oneCms,
          description: "One CMS service",
        },
        assetRendering: {
          name: "assetRendering",
          baseURL: apiEndpoints.assetRendering,
          description: "Asset rendering service",
        },
        masterData: {
          name: "masterData",
          baseURL: apiEndpoints.masterData,
          description: "Master data service",
        },
        tenantManagmant: {
          name: "tenantManagmant",
          baseURL: apiEndpoints.tenantManagement,
          description: "Tenant management service",
        },
        newQB: {
          name: "newQB",
          baseURL: apiEndpoints.newQB,
          description: "New Question Bank service",
        },
        assessment: {
          name: "assessment",
          baseURL: apiEndpoints.assessment,
          description: "Assessment service",
        },
        oneAsm: {
          name: "oneAsm",
          baseURL: apiEndpoints.oneAsm,
          description: "One Assessment service",
        },
        doubts: {
          name: "doubts",
          baseURL: apiEndpoints.doubts,
          description: "Doubts service",
        },
        doubtsQB: {
          name: "doubtsQB",
          baseURL: apiEndpoints.doubtsQB,
          description: "Doubts Question Bank",
        },
        doubtsAi: {
          name: "doubtsAi",
          baseURL: apiEndpoints.doubtsAI,
          description: "Doubts AI service",
        },
        doubtsDCAPI: {
          name: "doubtsDCAPI",
          baseURL: apiEndpoints.doubtsDCAPI,
          description: "Doubts DCAPI service",
        },
        wizklub: {
          name: "wizklub",
          baseURL: apiEndpoints.wizklub,
          description: "Wizklub service",
        },
        peekmind: {
          name: "peekmind",
          baseURL: apiEndpoints.peekmind,
          description: "Peekmind service",
        },
        peekMindReport: {
          name: "peekMindReport",
          baseURL: apiEndpoints.peekMindReport,
          description: "Peekmind report service",
        },
        peekmindOnResume: {
          name: "peekmindOnResume",
          baseURL: apiEndpoints.peekmindOnResume,
          description: "Peekmind resume service",
        },
        newPeekmindTiken: {
          name: "newPeekmindTiken",
          baseURL: apiEndpoints.newPeekmindToken,
          description: "New Peekmind token service",
        },
        newPeekmindQuiz: {
          name: "newPeekmindQuiz",
          baseURL: apiEndpoints.newPeekmindQuiz,
          description: "New Peekmind quiz service",
        },
        learn: {
          name: "learn",
          baseURL: apiEndpoints.learn,
          description: "Learn service",
        },
        liveclass: {
          name: "liveclass",
          baseURL: apiEndpoints.liveclass,
          description: "Live class service",
        },
        liveclassSocket: {
          name: "liveclassSocket",
          baseURL: apiEndpoints.liveclassSocket,
          description: "Live class websocket",
        },
        eos: {
          name: "eos",
          baseURL: apiEndpoints.eos,
          description: "EOS service",
        },
        s3Static: {
          name: "s3Static",
          baseURL: apiEndpoints.s3Static,
          description: "S3 Static files service",
        },
        oneApp: {
          name: "oneApp",
          baseURL: apiEndpoints.oneApp,
          description: "One App / One Learn service",
        },
        aina: {
          name: "aina",
          baseURL: apiEndpoints.aina,
          description: "AINA service",
        },
        uam: {
          name: "uam",
          baseURL: apiEndpoints.uam,
          description: "User Access Management",
        },
        myActivity: {
          name: "myActivity",
          baseURL: apiEndpoints.myActivity,
          description: "My Activity service",
        },
        dataReport: {
          name: "dataReport",
          baseURL: apiEndpoints.dataReport,
          description: "Data report service",
        },
        search: {
          name: "search",
          baseURL: apiEndpoints.search,
          description: "Search service",
        },
        selflearn: {
          name: "selflearn",
          baseURL: apiEndpoints.selflearn,
          description: "Self learn service",
        },
        selflearnWbeSocket: {
          name: "selflearnWbeSocket",
          baseURL: apiEndpoints.selflearnWebSocket,
          description: "Self learn websocket",
        },
        playlistVideo: {
          name: "playlistVideo",
          baseURL: apiEndpoints.playlistVideo,
          description: "Playlist Video Service",
        },
        playlistReport: {
          name: "playlistReport",
          baseURL: apiEndpoints.playlistReport,
          description: "Playlist Report Service",
        },
        newasm: {
          name: "newasm",
          baseURL: apiEndpoints.newasm,
          description: "New ASM API service",
        },
        liveclassapiv2: {
          name: "liveclassapiv2",
          baseURL: apiEndpoints.liveclassapiv2,
          description: "Live class API v2 service",
        },
        lsqManagement: {
          name: "lsqManagement",
          baseURL: apiEndpoints.lsqManagement,
          description: "LSQ Management (LeadSquared)",
        },
        lsqService: {
          name: "lsqService",
          baseURL: apiEndpoints.lsqService,
          description: "LSQ Service (device attributes, etc.)",
        },
        oneGenius: {
          name: "oneGenius",
          baseURL: apiEndpoints.oneGenius,
          description: "One Genius (certificate API)",
        },
        testlms: {
          name: "testlms",
          baseURL: apiEndpoints.testlms,
          description: "TestLMS service",
        },
        assignmentReport: {
          name: "assignmentReport",
          baseURL: apiEndpoints.assignmentReport,
          description: "Assignment report service",
        },
      },
    }),
    tokenStorage: createTokenStorage({
      getAccessToken: () => {
        return getAccessToken();
      },
      getRefreshToken: () => useAuthStore.getState().refreshToken,
      setTokens: (accessToken, refreshToken) => {
        useAuthStore.getState().setTokens(accessToken, refreshToken);
      },
      clearTokens: () => {
        useAuthStore.getState().logout(false);
      },
    }),

    refreshTokenFn: async (): Promise<RefreshTokenResponse> => {
      const body = buildRefreshProfileTokenV3Body();
      const baseUrl = apiEndpoints.learnGateway.replace(/\/$/, "");
      const currentProgram = getCurrentProgram();
      const tenant_id = String(currentProgram?.tenant_id ?? "");
      const response = await fetch(
        `${baseUrl}/api/v3/unified/refresh-profile-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Page": "WEB_IL_SITE",
            "X-Product": "1",
            "tenant-id": tenant_id,
            platform: "web",
          },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = (await response.json()) as Record<string, unknown>;
      const access =
        (typeof data.access_token === "string" && data.access_token) ||
        (typeof data.accessToken === "string" && data.accessToken) ||
        (typeof data.token === "string" && data.token);

      if (!access) {
        throw new Error("Token refresh failed: no access token in response");
      }

      const nextRefresh =
        (typeof data.refresh_token === "string" && data.refresh_token) ||
        (typeof data.refreshToken === "string" && data.refreshToken) ||
        body.refresh_token;

      return {
        accessToken: access,
        refreshToken: nextRefresh,
        expiresIn:
          typeof data.expires_in === "number"
            ? data.expires_in
            : typeof data.expiresIn === "number"
              ? data.expiresIn
              : undefined,
      };
    },

    onAuthFailure: () => {
      useAuthStore.getState().logout(false);

      const currentPath = window.location.pathname;
      if (currentPath !== "/login") {
        window.location.href = "/login";
      }
    },

    onError: (error: ApiError) => {
      if (isDev) {
        console.error("[API Error]", error.type, error.message, error.details);
      }

      if (error.type === ApiErrorType.RATE_LIMITED) {
        console.warn("[API] Rate limited - too many requests");
      } else if (error.type === ApiErrorType.NETWORK_ERROR) {
        console.warn("[API] Network error - check your connection");
      }
    },

    metrics: {
      onTokenRefreshStart: () => {
        if (isDev) {
          console.log("[API Metrics] Token refresh started");
        }
      },
      onTokenRefreshSuccess: (duration) => {
        if (isDev) {
          console.log(`[API Metrics] Token refresh succeeded in ${duration}ms`);
        }
      },
      onTokenRefreshFailure: (error) => {
        console.error("[API Metrics] Token refresh failed:", error);
      },
      onRequestQueued: (count) => {
        if (isDev && count > 0) {
          console.log(
            `[API Metrics] ${count} request(s) queued during token refresh`,
          );
        }
      },
      onRequestRetry: (attempt, statusCode) => {
        if (isDev) {
          console.log(
            `[API Metrics] Retrying request (attempt ${attempt}, status ${statusCode})`,
          );
        }
      },
    },

    enableLogging: isDev,

    retry: {
      count: 2,
      delay: 1000,
      statusCodes: [408, 500, 502, 503, 504],
    },

    timeout: 30000,

    crossTab: {
      enabled: true,
      channelName: "oneapp-auth",
      lockName: "oneapp-token-refresh",
      lockTimeout: 10000,
    },

    headerRules: [
      createHeaderRule({
        pattern: /generate-otp/,
        remove: ["Authorization"],
        priority: 100,
      }),
      createHeaderRule({
        pattern: /login-with-otp/,
        remove: ["Authorization"],
        priority: 100,
      }),
      createHeaderRule({
        pattern: /login-with-admission-id/,
        remove: ["Authorization"],
        priority: 100,
      }),

      createHeaderRule({
        pattern: /one_asm_api|one_asm_asset_api|proctoringUrl|geojs/,
        remove: [
          "X-Tenant",
          "ot-tenant",
          "X-Platform",
          "X-DeviceId",
          "Authorization",
        ],
        priority: 100,
      }),

      // Unified gateway (learn) validates lowercase `platform` on many routes (e.g. deactivate-user)
      createHeaderRule({
        pattern: /\/api\/v[0-9]+\/unified\//,
        add: {
          platform: "web",
        },
        priority: 50,
      }),

      // Updated rule to match all oneApp URLs across environments
      createHeaderRule({
        pattern:
          /oneapp(staginglambda|api)?\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          "X-Tenant": () => getTenant(),
          "ot-tenant": () => getTenant(),
          "X-Platform": "web",
          platform: "web",
          "X-DeviceId": () => getDeviceId(),
          accesstoken: () => getAccessToken(),
          Authorization: () => getBearerToken(),
          country_code: "IND",
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern:
          /selflearnapi\.devinfinitylearn\.net|selflearnv2(stg)?api\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          subTenantId: () => String(getCurrentProgram()?.subtenant_id ?? ""),
          tenantId: () => String(getCurrentProgram()?.tenant_id ?? ""),
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern: /lcapi\.oneapp\.com/,
        add: {
          "X-Tenant": () => getTenant(),
          "ot-tenant": () => getTenant(),
          "X-Platform": "web",
          "X-DeviceId": () => getDeviceId(),
          userid: () => userId(),
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern: /api\/selflearn\/getMapping/,
        remove: ["X-Tenant", "ot-tenant", "X-Platform", "X-DeviceId"],
        priority: 100,
      }),

      createHeaderRule({
        pattern: /s3\.ap-south-1\.amazonaws\.com/,
        remove: [
          "Authorization",
          "X-Tenant",
          "ot-tenant",
          "X-Platform",
          "X-DeviceId",
        ],
        priority: 100,
      }),
      createHeaderRule({
        pattern:
          /assets\.infinitylearn\.com\/scorerevamp\/homepage\/json\/il_score_about_test\.json/,
        remove: [
          "Authorization",
          "X-Tenant",
          "ot-tenant",
          "X-Platform",
          "X-DeviceId",
        ],
        priority: 100,
      }),

      createHeaderRule({
        pattern: /cms.*api|communityapi|onecms/,
        add: {
          "X-Tenant": () => getTenant(),
          "ot-tenant": () => getTenant(),
          platform: "web",
          "X-DeviceId": () => getDeviceId(),
          isMultiSessionRequired: "true",
          userid: () => userId(),
          Authorization: () => getBearerToken(),
          accesstoken: () => getAccessToken(),
          subTenantId: () => String(getCurrentProgram()?.subtenant_id ?? ""),
          Accept: "application/json",
        },
        priority: 50,
      }),

      // Doubts AI (meta/subjects, etc.) — program-aligned tenentId (see getTenentIdForDoubtsApis).
      createHeaderRule({
        pattern:
          /doubtsai(-staging)?\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          isMultiSessionRequired: "true",
          platform: "web",
          "x-platform": "web",
          "x-tenant": () => getTenant(),
          "ot-tenant": () => getTenant(),
          tenant: () => getTenant(),
          tenentId: () => getTenentIdForDoubtsApis(),
          accesstoken: () => getAccessToken(),
          Authorization: () => getBearerToken(),
          "X-DeviceId": () => getDeviceId(),
        },
        priority: 55,
      }),

      // DCAPI — preprod working trace: Bearer + tenant/platform/x-* only (no accesstoken header).
      createHeaderRule({
        pattern: /dcapi(-staging)?\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          Accept: "application/json, text/plain, */*",
          Authorization: () => getBearerToken(),
          "ot-tenant": () => getTenant(),
          platform: "web",
          tenant: () => getTenant(),
          "x-platform": "web",
          "x-tenant": () => getTenant(),
          "x-deviceid": () => getDeviceId(),
        },
        priority: 55,
      }),

      createHeaderRule({
        pattern: /myactivity.*\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          "X-Tenant": "infinitylearn",
          "ot-tenant": "infinitylearn",
          "X-Platform": "web",
          "X-DeviceId": () => getDeviceId(),
          accesstoken: () => getAccessToken(),
          Authorization: () => getBearerToken(),
          tenantid: "1",
        },
        priority: 50,
      }),
      createHeaderRule({
        pattern: /omsapi.*\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          "x-tenant": "infinitylearn",
          "ot-tenant": "infinitylearn",
          "x-platform": "web",
          "x-deviceid": () => getDeviceId(),
          authorization: () => getBearerToken(),
          accept: "application/json, text/plain, */*",
        },
        priority: 50,
      }),
      createHeaderRule({
        pattern: /omsstagingapi.*\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          "x-tenant": "infinitylearn",
          "ot-tenant": "infinitylearn",
          "x-platform": "web",
          "x-deviceid": () => getDeviceId(),
          authorization: () => getBearerToken(),
          accept: "application/json, text/plain, */*",
        },
        priority: 50,
      }),
      createHeaderRule({
        pattern:
          /(oneoms.*\.(devinfinitylearn\.in|infinitylearn\.com))|(staging-api\.example\.com)/,
        add: {
          "x-tenant": "infinitylearn",
          "ot-tenant": "infinitylearn",
          "x-platform": "web",
          "x-deviceid": () => getDeviceId(),
          authorization: () => getBearerToken(),
          "x-source-id": "4",
          "auth-token": () => getBearerToken(),
          accept: "application/json, text/plain, */*",
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern:
          /(newasmapi|newasmstageapi).*\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          accept: "application/json, text/plain, */*",
          "accept-language": "en,en-US;q=0.9",
          "ot-tenant": "infinitylearn",
          "x-tenant": "infinitylearn",
          "x-deviceid": () => getDeviceId(),
          "x-platform": "web",
          student: () => userId(),
          Authorization: () => getBearerToken(),
          accesstoken: () => getAccessToken(),
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern: /u7vrkapm9i\.execute-api\.ap-south-1\.amazonaws\.com/,
        add: {
          accept: "application/json, text/plain, */*",
          "accept-language": "en,en-US;q=0.9",
          "ot-tenant": "infinitylearn",
          student: () => userId(),
          Authorization: () => getBearerToken(),
          accesstoken: () => getAccessToken(),
          "x-deviceid": () => getDeviceId(),
          "x-platform": "web",
          "x-tenant": "infinitylearn",
        }, // Headers are handled locally in PlaylistDetailPage.tsx
        priority: 50,
      }),
      createHeaderRule({
        pattern: /analytics.*\.(devinfinitylearn\.in|infinitylearn\.com)/,
        add: {
          Accept: "application/json, text/plain, */*",
          Authorization: () => getBearerToken(),
          "x-auth-token": () => getAccessToken(),
          "ot-tenant": () => getTenant().toLowerCase(),
          "x-tenant": () => getTenant().toLowerCase(),
          platform: "web",
          "x-platform": "web",
          "x-deviceid": () => getDeviceId(),
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern:
          /(lsqmanagement(preprod)?\.devinfinitylearn\.in|lsqmanagement\.dev\.infinitylearn\.in|lsqmanagementservice\.infinitylearn\.com)/,
        add: {
          Accept: "application/json",
          "Content-Type": "application/json",
          platform: "web",
          Authorization: () => getBearerToken(),
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern:
          /onegenius(staging|preprod)?\.(devinfinitylearn\.in|infinitylearn\.com)|onegeniusapi\.infinitylearn\.com/,
        add: {
          Accept: "application/json",
          "Content-Type": "application/json",
          platform: "web",
          "x-platform": "web",
          Authorization: () => getBearerToken(),
          accesstoken: () => getAccessToken(),
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern: /lsqservice\.infinitylearn\.com/,
        add: {
          Accept: "application/json",
          "Content-Type": "application/json",
          platform: "web",
        },
        priority: 50,
      }),

      createHeaderRule({
        pattern:
          /testlms\.(ilteach\.com|devinfinitylearn\.in)|prodslms\.ilteacher\.com/,
        add: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
          "ot-tenant": getTenant(),
          "X-Tenant": getTenant(),
          "X-Platform": "web",
          "x-platform": "web",
          "X-DeviceId": () => getDeviceId(),
          "x-deviceid": () => getDeviceId(),
          accesstoken: () => getAccessToken(),
          Authorization: () => getBearerToken(),
        },
        priority: 50,
      }),
    ],
  });
}
